"use strict";
/**
  * @reach/checkbox v0.18.0
  *
  * Copyright (c) 2018-2022, React Training LLC
  *
  * This source code is licensed under the MIT license found in the
  * LICENSE.md file in the root directory of this source tree.
  *
  * @license MIT
  */


// src/custom.tsx
import * as React2 from "react";
import {
  createContext,
  isFunction,
  useComposedRefs as useComposedRefs2,
  composeEventHandlers as composeEventHandlers2
} from "@reach/utils";

// src/mixed.tsx
import * as React from "react";
import {
  composeEventHandlers,
  useIsomorphicLayoutEffect,
  useComposedRefs
} from "@reach/utils";
import { assign, useCreateMachine, useMachine } from "@reach/machine";
var DEBUG = false;
var MixedCheckboxStates = /* @__PURE__ */ ((MixedCheckboxStates2) => {
  MixedCheckboxStates2["Checked"] = "checked";
  MixedCheckboxStates2["Mixed"] = "mixed";
  MixedCheckboxStates2["Unchecked"] = "unchecked";
  return MixedCheckboxStates2;
})(MixedCheckboxStates || {});
var MixedCheckboxEvents = /* @__PURE__ */ ((MixedCheckboxEvents2) => {
  MixedCheckboxEvents2["GetDerivedData"] = "GET_DERIVED_DATA";
  MixedCheckboxEvents2["Mount"] = "MOUNT";
  MixedCheckboxEvents2["Set"] = "SET";
  MixedCheckboxEvents2["Reset"] = "RESET";
  MixedCheckboxEvents2["Toggle"] = "TOGGLE";
  MixedCheckboxEvents2["Unmount"] = "UNMOUNT";
  return MixedCheckboxEvents2;
})(MixedCheckboxEvents || {});
function checkToggleAllowed(data) {
  return !!(data && !data.isControlled && !data.disabled);
}
function getCheckSetCondition(state) {
  return function(data, event) {
    return data && data.isControlled && event.state === state;
  };
}
function getCheckResetCondition(state) {
  return function(data, event) {
    return data && !data.isControlled && data.initialState === state;
  };
}
var assignRefs = assign((data, event) => {
  return {
    ...data,
    refs: event.refs
  };
});
var commonEvents = {
  ["MOUNT" /* Mount */]: {
    actions: assignRefs
  },
  ["GET_DERIVED_DATA" /* GetDerivedData */]: {
    actions: [
      assignRefs,
      assign((data, event) => {
        return {
          ...data,
          ...event.data
        };
      })
    ]
  },
  ["SET" /* Set */]: [
    {
      target: "checked" /* Checked */,
      cond: getCheckSetCondition("checked" /* Checked */)
    },
    {
      target: "unchecked" /* Unchecked */,
      cond: getCheckSetCondition("unchecked" /* Unchecked */)
    },
    {
      target: "mixed" /* Mixed */,
      cond: getCheckSetCondition("mixed" /* Mixed */)
    }
  ],
  ["RESET" /* Reset */]: [
    {
      target: "checked" /* Checked */,
      cond: getCheckResetCondition("checked" /* Checked */)
    },
    {
      target: "unchecked" /* Unchecked */,
      cond: getCheckResetCondition("unchecked" /* Unchecked */)
    },
    {
      target: "mixed" /* Mixed */,
      cond: getCheckResetCondition("mixed" /* Mixed */)
    }
  ]
};
var createMachineDefinition = (initialState, props) => ({
  id: "mixed-checkbox",
  initial: initialState,
  context: {
    disabled: props.disabled,
    initialState,
    isControlled: props.isControlled,
    refs: {
      input: null
    }
  },
  states: {
    ["unchecked" /* Unchecked */]: {
      entry: assignRefs,
      on: {
        ["TOGGLE" /* Toggle */]: {
          target: "checked" /* Checked */,
          cond: checkToggleAllowed
        },
        ...commonEvents
      }
    },
    ["checked" /* Checked */]: {
      entry: assignRefs,
      on: {
        ["TOGGLE" /* Toggle */]: {
          target: "unchecked" /* Unchecked */,
          cond: checkToggleAllowed
        },
        ...commonEvents
      }
    },
    ["mixed" /* Mixed */]: {
      entry: assignRefs,
      on: {
        ["TOGGLE" /* Toggle */]: {
          target: "checked" /* Checked */,
          cond: checkToggleAllowed
        },
        ...commonEvents
      }
    }
  }
});
var MixedCheckbox = React.forwardRef(function MixedCheckbox2({ as: Comp = "input", checked, defaultChecked, disabled, onChange, ...props }, forwardedRef) {
  let ownRef = React.useRef(null);
  let ref = useComposedRefs(forwardedRef, ownRef);
  let [inputProps] = useMixedCheckbox(ownRef, {
    onChange,
    checked,
    defaultChecked,
    disabled
  }, "MixedCheckbox");
  return /* @__PURE__ */ React.createElement(Comp, {
    ...props,
    ...inputProps,
    "data-reach-mixed-checkbox": "",
    ref
  });
});
MixedCheckbox.displayName = "MixedCheckbox";
function useMixedCheckbox(inputRef, args, deprecated_callerName) {
  let callerName = args?.callerName || deprecated_callerName || "useMixedCheckbox";
  let {
    checked: controlledChecked,
    defaultChecked,
    disabled,
    onChange,
    onClick
  } = args || {};
  let isControlled = controlledChecked != null;
  let machine = useCreateMachine(createMachineDefinition(checkedPropToStateValue(isControlled ? controlledChecked : defaultChecked), {
    disabled: !!disabled,
    isControlled
  }));
  let [current, send] = useMachine(machine, { input: inputRef }, DEBUG);
  let props = {
    checked: stateValueToChecked(current.value),
    disabled: !!disabled,
    onChange: composeEventHandlers(onChange, handleChange),
    onClick: composeEventHandlers(onClick, handleClick),
    type: "checkbox"
  };
  if (current.value === "mixed" /* Mixed */) {
    props["aria-checked"] = "mixed";
  }
  let contextData = {
    checked: current.value === "checked" /* Checked */ ? true : current.value === "mixed" /* Mixed */ ? "mixed" : false
  };
  function handleChange() {
    if (!isControlled) {
      send("TOGGLE" /* Toggle */);
    }
  }
  function handleClick() {
    syncDomNodeWithState();
  }
  function syncDomNodeWithState() {
    if (inputRef.current) {
      inputRef.current.indeterminate = current.value === "mixed" /* Mixed */;
    }
  }
  if (true) {
    let { current: wasControlled } = React.useRef(isControlled);
    React.useEffect(() => {
      if (!inputRef.current) {
        console.warn(`A ref was not assigned to an input element in ${callerName}.`);
      }
    }, [inputRef, callerName]);
    React.useEffect(() => {
      if (!isControlled && wasControlled) {
        console.warn(`${callerName} is changing from controlled to uncontrolled. ${callerName} should not switch from controlled to uncontrolled (or vice versa). Decide between using a controlled or uncontrolled ${callerName} for the lifetime of the component. Check the \`checked\` prop being passed in.`);
      }
      if (isControlled && !wasControlled) {
        console.warn(`${callerName} is changing from uncontrolled to controlled. ${callerName} should not switch from uncontrolled to controlled (or vice versa). Decide between using a controlled or uncontrolled ${callerName} for the lifetime of the component. Check the \`checked\` prop being passed in.`);
      }
    }, [callerName, isControlled, wasControlled]);
  }
  React.useEffect(() => {
    if (isControlled) {
      send({
        type: "SET" /* Set */,
        state: checkedPropToStateValue(controlledChecked)
      });
    }
  }, [isControlled, controlledChecked, send]);
  React.useEffect(() => {
    let form = inputRef.current?.form;
    if (!form)
      return;
    function handleReset(event) {
      send({ type: "RESET" /* Reset */ });
    }
    form.addEventListener("reset", handleReset);
    return () => {
      form?.removeEventListener("reset", handleReset);
    };
  }, [inputRef, isControlled, send]);
  useIsomorphicLayoutEffect(syncDomNodeWithState);
  React.useEffect(() => {
    send({
      type: "GET_DERIVED_DATA" /* GetDerivedData */,
      data: {
        disabled,
        isControlled
      }
    });
  }, [disabled, isControlled, send]);
  return [props, contextData];
}
function checkedPropToStateValue(checked) {
  return checked === true ? "checked" /* Checked */ : checked === "mixed" ? "mixed" /* Mixed */ : "unchecked" /* Unchecked */;
}
function stateValueToChecked(state) {
  return state === "checked" /* Checked */ ? true : false;
}

// src/custom.tsx
var [CustomCheckboxContextProvider, useCustomCheckboxContext] = createContext("CustomCheckboxContext", {});
var CustomCheckboxContainer = React2.forwardRef(function CustomCheckboxContainer2({
  as: Comp = "span",
  checked: controlledChecked,
  children,
  defaultChecked,
  disabled,
  onClick,
  onChange,
  __componentName = "CustomCheckboxContainer",
  ...props
}, forwardedRef) {
  let inputRef = React2.useRef(null);
  let [inputProps, stateData] = useMixedCheckbox(inputRef, {
    defaultChecked,
    checked: controlledChecked,
    disabled,
    onChange
  }, __componentName);
  let [focused, setFocused] = React2.useState(false);
  function handleClick() {
    window.requestAnimationFrame(() => {
      inputRef.current && inputRef.current.focus();
    });
  }
  let context = {
    defaultChecked,
    disabled,
    focused,
    inputProps,
    inputRef,
    setFocused
  };
  return /* @__PURE__ */ React2.createElement(CustomCheckboxContextProvider, {
    ...context
  }, /* @__PURE__ */ React2.createElement(Comp, {
    ...props,
    ref: forwardedRef,
    "data-reach-custom-checkbox-container": "",
    "data-focused": focused ? "" : void 0,
    "data-state": checkedPropToStateValue(stateData.checked),
    onClick: composeEventHandlers2(onClick, handleClick)
  }, isFunction(children) ? children({
    checked: stateData.checked,
    inputRef,
    focused
  }) : children));
});
CustomCheckboxContainer.displayName = "CustomCheckboxContainer";
var CustomCheckboxInput = React2.forwardRef(function CustomCheckboxInput2({ as: Comp = "input", onBlur, onFocus, ...props }, forwardedRef) {
  let { focused, inputProps, inputRef, setFocused } = useCustomCheckboxContext("CustomCheckboxInput");
  let ref = useComposedRefs2(forwardedRef, inputRef);
  let mounted = React2.useRef(true);
  function handleBlur() {
    window.requestAnimationFrame(() => {
      if (mounted.current) {
        setFocused(false);
      }
    });
  }
  function handleFocus() {
    window.requestAnimationFrame(() => {
      if (mounted.current) {
        setFocused(true);
      }
    });
  }
  React2.useEffect(() => () => void (mounted.current = false), []);
  return /* @__PURE__ */ React2.createElement(Comp, {
    ...props,
    ...inputProps,
    ref,
    type: "checkbox",
    "data-reach-custom-checkbox-input": "",
    "data-focused": focused ? "" : void 0,
    onBlur: composeEventHandlers2(onBlur, handleBlur),
    onFocus: composeEventHandlers2(onFocus, handleFocus)
  });
});
CustomCheckboxInput.displayName = "CustomCheckboxInput";
var CustomCheckbox = React2.forwardRef(function CustomCheckbox2({ children, id, name, value, ...props }, forwardedRef) {
  return /* @__PURE__ */ React2.createElement(CustomCheckboxContainer, {
    ...props,
    "data-reach-custom-checkbox": "",
    __componentName: "CustomCheckbox"
  }, /* @__PURE__ */ React2.createElement(CustomCheckboxInput, {
    id,
    name,
    ref: forwardedRef,
    value
  }), children);
});
CustomCheckbox.displayName = "CustomCheckbox";
export {
  CustomCheckbox,
  CustomCheckboxContainer,
  CustomCheckboxInput,
  MixedCheckbox,
  MixedCheckboxEvents,
  MixedCheckboxStates,
  checkedPropToStateValue as internal_checkedPropToStateValue,
  useMixedCheckbox
};
